'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

interface ProfFormData {
  email: string;
  password: string;
  orgId: string;
}

export default function ProfessionalLoginForm({ isSignUp }: { isSignUp: boolean }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, formState: { errors } } = useForm<ProfFormData>();

  const onSubmit = async (_data: ProfFormData) => {
    setLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/professional with email, password, orgId
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    router.push('/student-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div>
        <label htmlFor="prof-email" className="block text-sm font-semibold text-foreground mb-1.5">Work Email</label>
        <input
          id="prof-email"
          type="email"
          {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
          placeholder="nadia.volkov@jarvondisu.edu"
          className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
        />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
      </div>

      {isSignUp && (
        <div>
          <label htmlFor="prof-orgid" className="block text-sm font-semibold text-foreground mb-1.5">Organization ID</label>
          <p className="text-xs text-muted-foreground mb-2">Provided by your institution or employer. Leave blank for individual enrollment.</p>
          <input
            id="prof-orgid"
            type="text"
            {...register('orgId')}
            placeholder="ORG-2026-XXXX"
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground font-mono placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
          />
        </div>
      )}

      <div>
        <label htmlFor="prof-password" className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
        <div className="relative">
          <input
            id="prof-password"
            type={showPassword ? 'text' : 'password'}
            {...register('password', { required: 'Password is required' })}
            placeholder="••••••••"
            className="w-full px-4 py-2.5 pr-10 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 bg-amber-600 text-white font-semibold rounded-lg btn-scale hover:bg-amber-700 transition-all disabled:opacity-60"
        style={{ minHeight: '44px' }}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? 'Entering Portal...' : isSignUp ? 'Create Professional Account' : 'Enter Professional Portal'}
      </button>
    </form>
  );
}